import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/common/SEO";
import { ROUTES } from "../../constants/routes";
import transparentLogo from "../../assets/images/Transparent_logo.png";
import { ShieldIcon } from "../../components/common/Icons";

const SECTIONS = [
  { id: "acceptance", num: "01", title: "Acceptance & Eligibility" },
  { id: "accounts", num: "02", title: "Account Registration & Security" },
  { id: "fair-play", num: "03", title: "Contests & Fair Play Rules" },
  { id: "scoring-rankings", num: "04", title: "Scoring, Streaks & Leaderboards" },
  { id: "host-studio", num: "05", title: "Host Studio & Church Leagues" },
  { id: "community-conduct", num: "06", title: "User Content & Community Conduct" },
  { id: "intellectual-property", num: "07", title: "Intellectual Property & Scripture" },
  { id: "prizes-badges", num: "08", title: "Virtual Badges & Contest Prizes" },
  { id: "termination-erasure", num: "09", title: "Suspension & Account Erasure" },
  { id: "disclaimers", num: "10", title: "Service Availability & Latency" },
  { id: "liability", num: "11", title: "Limitation of Liability" },
  { id: "governing-law", num: "12", title: "Governing Law & Contact Info" },
];

const TermsAndConditionsPage = () => {
  const [activeSection, setActiveSection] = useState("acceptance");

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 120;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(SECTIONS[i].id);
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(SECTIONS[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="legal-doc-page">
      <SEO
        title="Terms & Conditions — Bible Contest"
        description="Official Terms and Conditions and Tournament Rules for the Bible Contest web platform, mobile applications, and church league tournaments. Understand eligibility, anti-cheating rules, and player rights."
        canonicalUrl="/terms-and-conditions"
      />

      {/* ─── Top Brand Navigation ─── */}
      <header className="privacy-top-navbar">
        <div className="privacy-nav-container">
          <Link to="/" className="privacy-nav-brand">
            <img src={transparentLogo} alt="Bible Contest Logo" className="privacy-nav-logo" />
            <span className="privacy-nav-title">Bible Contest</span>
            <span className="privacy-nav-badge">Legal Center</span>
          </Link>

          <div className="privacy-nav-links">
            <Link to="/support" className="privacy-nav-link">
              Need Help?
            </Link>
            <Link to="/" className="privacy-nav-link" style={{ color: "var(--clr-primary)" }}>
              <span>Return to App →</span>
            </Link>
          </div>
        </div>
      </header>

      {/* ─── Hero Section ─── */}
      <section className="privacy-hero">
        <div className="privacy-hero-pill">
          <ShieldIcon size={14} />
          <span>Fair Play, Integrity & Community Tournament Rules</span>
        </div>
        <h1 style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.4rem)", fontWeight: 800, margin: "0 0 12px 0" }}>
          Terms & Conditions of Service
        </h1>
        <p style={{ fontSize: "15px", color: "var(--clr-text-2)", lineHeight: 1.6, margin: 0 }}>
          Welcome to Bible Contest. These Terms and Conditions constitute a legally binding agreement between you and Bible Contest (“We”, “Us”, or “Our”) governing your access to and use of our web portal, mobile applications (iOS and Android), and host league management tools.
        </p>
      </section>

      {/* ─── Quick Legal Switcher Tabs ─── */}
      <div className="legal-quick-switcher">
        <Link to={ROUTES.LEGAL.PRIVACY} className="legal-switcher-tab">
          <span>📜</span>
          <span>Privacy Policy</span>
        </Link>
        <Link to={ROUTES.LEGAL.TERMS} className="legal-switcher-tab active">
          <span>⚖️</span>
          <span>Terms & Conditions</span>
        </Link>
        <Link to={ROUTES.LEGAL.DELETION_REQUEST} className="legal-switcher-tab">
          <span>🛡️</span>
          <span>Account Erasure Portal</span>
        </Link>
      </div>

      {/* ─── Mobile Horizontal Quick Jump ─── */}
      <div className="legal-mobile-quick-jump">
        {SECTIONS.map((sec) => (
          <a key={sec.id} href={`#${sec.id}`} className="legal-mobile-pill">
            {sec.num}. {sec.title}
          </a>
        ))}
      </div>

      {/* ─── 2-Column Document Layout ─── */}
      <div className="legal-doc-layout">
        {/* Left Sticky Table of Contents */}
        <aside className="legal-sidebar">
          <div className="legal-toc-card">
            <div className="legal-toc-header">
              <h3 className="legal-toc-title">Table of Contents</h3>
              <button type="button" className="legal-print-btn" onClick={handlePrint}>
                <span>🖨️</span>
                <span>Print</span>
              </button>
            </div>
            <nav className="legal-toc-list">
              {SECTIONS.map((sec) => (
                <a
                  key={sec.id}
                  href={`#${sec.id}`}
                  className={`legal-toc-item ${activeSection === sec.id ? "active" : ""}`}
                >
                  <span className="legal-toc-num">{sec.num}</span>
                  <span>{sec.title}</span>
                </a>
              ))}
            </nav>
          </div>

          <div className="legal-sidebar-help">
            <div className="legal-sidebar-help-title">
              <span>⚖️</span>
              <span>Need Clarification?</span>
            </div>
            <p style={{ margin: 0, color: "var(--clr-text-2)", lineHeight: 1.5 }}>
              Questions about tournament eligibility or host rules? Contact our legal & compliance desk:
            </p>
            <a href="mailto:support@biblecontest.app" style={{ color: "var(--clr-primary)", fontWeight: 700, textDecoration: "none" }}>
              support@biblecontest.app
            </a>
          </div>

          <div className="legal-sidebar-help" style={{ background: "#F0FDF4", borderColor: "#BBF7D0" }}>
            <div className="legal-sidebar-help-title" style={{ color: "#15803D" }}>
              <span>✓</span>
              <span>Account Erasure Rights</span>
            </div>
            <p style={{ margin: 0, color: "#166534", lineHeight: 1.5 }}>
              You may terminate these terms and request complete deletion of your account and scores at any time.
            </p>
            <Link to={ROUTES.LEGAL.DELETION_REQUEST} style={{ color: "#15803D", fontWeight: 700, textDecoration: "underline" }}>
              Visit Erasure Portal →
            </Link>
          </div>
        </aside>

        {/* Right Main Legal Article */}
        <main className="legal-main-content">
          <article className="legal-card">
            {/* Metadata Bar */}
            <div className="legal-meta-bar">
              <div className="legal-meta-item">
                <span>Effective Date:</span>
                <strong>October 22, 2026</strong>
              </div>
              <div className="legal-meta-item">
                <span>Last Updated:</span>
                <strong>October 2026</strong>
              </div>
              <div className="legal-meta-item">
                <span>Version:</span>
                <strong>3.2 (Production Standard)</strong>
              </div>
              
            </div>

            {/* Section 01: Acceptance & Eligibility */}
            <section id="acceptance" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">01</span>
                <h2 className="legal-section-title">Acceptance of Terms & Eligibility</h2>
              </div>
              <div className="legal-prose">
                <p>
                  By creating an account, downloading or accessing the Bible Contest mobile applications (available on the Google Play Store and Apple App Store), or using the web platform at <strong>biblecontest.app</strong>, you confirm that you have read, understood, and agree to be bound by these Terms and Conditions.
                </p>
                <div className="legal-summary-box">
                  <div className="legal-summary-box-title">
                    <span>💡</span> Key Highlights
                  </div>
                  Bible Contest is an educational, faith-enriching platform designed to foster regular Scripture reading and Christian fellowship. We maintain zero tolerance for cheating, abusive behavior, and unfair gameplay.
                </div>
                <h4 style={{ margin: "16px 0 8px 0", color: "var(--clr-text-1)" }}>Eligibility Criteria:</h4>
                <ul>
                  <li><strong>Minimum Age:</strong> You must be at least 13 years of age (or the applicable minimum digital consent age in your country) to create an independent account. Children under 13 may participate under the direct supervision of a parent, legal guardian, or recognized Sunday School ministry coordinator.</li>
                  <li><strong>Territorial Availability:</strong> Bible Contest is accessible worldwide, provided such access complies with your local jurisdiction’s laws and Internet regulations.</li>
                  <li><strong>Capacity to Contract:</strong> You represent and warrant that you possess the full legal capacity to enter into this binding agreement.</li>
                </ul>
              </div>
            </section>

            {/* Section 02: Account Registration & Security */}
            <section id="accounts" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">02</span>
                <h2 className="legal-section-title">Account Registration & Security</h2>
              </div>
              <div className="legal-prose">
                <p>
                  To participate in live contests, record devotion streaks, earn badges, and appear on leaderboards, you must register an account using Firebase Authentication (Google Sign-In or verified Email & Password).
                </p>
                <ul>
                  <li><strong>Accurate Information:</strong> You agree to provide truthful, accurate, and current information regarding your display name, country of residence, and contact details. Impersonation of other contestants or ministry leaders is strictly prohibited.</li>
                  <li><strong>One Person, One Account:</strong> To preserve tournament integrity, each contestant is permitted only one primary active account. Registering duplicate or “smurf” accounts to manipulate rankings, replay contests, or farm referral rewards is grounds for immediate disqualification and account termination.</li>
                  <li><strong>Credential Confidentiality:</strong> You are solely responsible for maintaining the confidentiality of your account credentials. You must notify us immediately at <a href="mailto:support@biblecontest.app">support@biblecontest.app</a> if you suspect any unauthorized access or breach of security.</li>
                  <li><strong>Account Activity:</strong> All actions, submissions, and contest attempts originating from your authenticated credentials will be deemed executed by you.</li>
                </ul>
              </div>
            </section>

            {/* Section 03: Contests & Fair Play Rules */}
            <section id="fair-play" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">03</span>
                <h2 className="legal-section-title">Contests & Strict Fair Play Rules</h2>
              </div>
              <div className="legal-prose">
                <p>
                  Bible Contest tournaments and daily challenges are designed to measure knowledge, speed, and devotion to God’s Word. The integrity of our global leaderboards depends upon the honest participation of every contestant.
                </p>

                <div className="legal-summary-box" style={{ background: "#FEF2F2", borderLeftColor: "#EF4444" }}>
                  <div className="legal-summary-box-title" style={{ color: "#B91C1C" }}>
                    <span>⚠️</span> Zero Tolerance Anti-Cheating Policy
                  </div>
                  Any attempt to gain an artificial, automated, or unfair advantage undermines the Christian spirit of the competition. Violators are subject to permanent ranking removal and platform ban.
                </div>

                <h4 style={{ margin: "16px 0 8px 0", color: "var(--clr-text-1)" }}>Prohibited Conduct Includes:</h4>
                <ul>
                  <li><strong>Automated Scripts & Bots:</strong> Using automated scripts, emulator macros, browser extensions, or automated tools to answer questions or scrape question banks.</li>
                  <li><strong>Artificial Intelligence Assistance:</strong> Feeding question text or images to external AI assistants (e.g., ChatGPT, Claude, Gemini) or screen-reading tools during active, timed contest sessions.</li>
                  <li><strong>Collusion & Answer Leakage:</strong> Sharing contest answers, question screen captures, or solutions via social media, chat groups, or private messaging before a contest’s official finish window has elapsed.</li>
                  <li><strong>Client-Side Tampering:</strong> Modifying client JavaScript bundles, injecting API payloads, manipulating countdown clocks, or spoofing network latency timestamps.</li>
                  <li><strong>Multiple Device Play:</strong> Logging into the same contest simultaneously from multiple browser tabs or devices to test answers before submitting.</li>
                </ul>
                <p>
                  Our anti-fraud systems continuously monitor response time patterns, click anomalies, and statistical anomalies. Contest administrators reserve the right to void suspect scores without prior notice.
                </p>
              </div>
            </section>

            {/* Section 04: Scoring, Streaks & Leaderboards */}
            <section id="scoring-rankings" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">04</span>
                <h2 className="legal-section-title">Scoring, Streaks & Leaderboards</h2>
              </div>
              <div className="legal-prose">
                <p>
                  Tournament rankings and devotion achievements are determined by our standardized scoring algorithms:
                </p>

                <div className="legal-table-wrap">
                  <table className="legal-table">
                    <thead>
                      <tr>
                        <th>Mechanic</th>
                        <th>Standard Calculation Rules</th>
                        <th>Resolution & Disclaimers</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Accuracy Points</strong></td>
                        <td>Awarded for selecting the correct scriptural answer within the allotted countdown window (e.g., 10 to 30 seconds per question).</td>
                        <td>Incorrect or unanswered questions within the time limit receive 0 points. Partial credit is not supported.</td>
                      </tr>
                      <tr>
                        <td><strong>Speed Bonus</strong></td>
                        <td>Additional points calculated based on the fraction of remaining time when the correct answer was submitted.</td>
                        <td>In the event of a tie in total points, the contestant with the lower aggregate answering time is ranked higher.</td>
                      </tr>
                      <tr>
                        <td><strong>Devotion Streaks</strong></td>
                        <td>Incremented each consecutive calendar day you complete at least one Bible challenge or daily devotion quiz.</td>
                        <td>Streaks reset to 0 if a challenge is missed during a 24-hour cycle calculated against your configured local timezone.</td>
                      </tr>
                      <tr>
                        <td><strong>Leaderboard Publication</strong></td>
                        <td>Display names, country flags, point totals, and avatar icons are publicly visible to all participants and hosts.</td>
                        <td>Private identifiers (email addresses, phone numbers) are never exposed on public leaderboards.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p>
                  <strong>Finality of Results:</strong> Once a contest’s status moves to <em>Finished</em> and the review period concludes, published leaderboard results and trophy allocations are final and non-negotiable.
                </p>
              </div>
            </section>

            {/* Section 05: Host Studio & Church Leagues */}
            <section id="host-studio" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">05</span>
                <h2 className="legal-section-title">Host Studio & Church League Organization</h2>
              </div>
              <div className="legal-prose">
                <p>
                  Authorized pastors, youth leaders, and Sunday school teachers may be granted access to the <strong>Host Studio</strong> to create custom quizzes, schedule private church leagues, and supervise local tournament brackets.
                </p>
                <ul>
                  <li><strong>Theological & Scriptural Soundness:</strong> Hosts agree to formulate questions that honor sound biblical principles and provide correct Scripture references (Book, Chapter, and Verse).</li>
                  <li><strong>Appropriate Content:</strong> Quizzes created through Host Studio must not contain sectarian attacks, commercial promotions, profanity, or defamatory statements.</li>
                  <li><strong>Contest Lifecycle Control:</strong> Hosts are responsible for advancing contests through scheduled stages (<em>Upcoming</em> → <em>Ongoing</em> → <em>Finished</em>). Status modifications must be confirmed and executed responsibly.</li>
                  <li><strong>Independent Church Prizes:</strong> If a local church or host offers physical gifts, book awards, or certificates to their congregation, Bible Contest acts solely as the technical scoring software. All reward fulfillment remains the sole responsibility of the respective church organization.</li>
                </ul>
              </div>
            </section>

            {/* Section 06: User Content & Community Conduct */}
            <section id="community-conduct" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">06</span>
                <h2 className="legal-section-title">User Content & Community Conduct</h2>
              </div>
              <div className="legal-prose">
                <p>
                  Bible Contest is a Christian community dedicated to mutual encouragement, spiritual growth, and the love of Jesus Christ. Users are expected to conduct themselves with Christ-like kindness, humility, and respect.
                </p>
                <p>
                  You agree that you will not post, submit, or transmit any user content (including display names, profile avatars, questions, or support inquiries) that:
                </p>
                <ul>
                  <li>Is profane, blasphemous, sexually explicit, obscene, or racially offensive;</li>
                  <li>Harasses, bullies, threatens, or demeans any contestant, leader, or administrator;</li>
                  <li>Promotes unauthorized commercial solicitations, multi-level marketing, or affiliate spam;</li>
                  <li>Infringes upon any patent, trademark, trade secret, copyright, or privacy rights of any third party;</li>
                  <li>Introduces malware, trojans, denial-of-service scripts, or harmful computer code.</li>
                </ul>
                <p>
                  We reserve the unilateral right, though not the obligation, to edit, reject, or remove any user content and to suspend or terminate offending user profiles immediately.
                </p>
              </div>
            </section>

            {/* Section 07: Intellectual Property & Scripture */}
            <section id="intellectual-property" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">07</span>
                <h2 className="legal-section-title">Intellectual Property & Scripture Translations</h2>
              </div>
              <div className="legal-prose">
                <p>
                  <strong>Platform Proprietary Rights:</strong> The Bible Contest application, branding, logos, graphics, user interface designs, sound effects, source code, question database compilations, and software algorithms are the exclusive property of Bible Contest and are protected by applicable intellectual property and copyright laws.
                </p>
                <p>
                  <strong>Scripture Translations:</strong> Bible Contest quotes Scripture passages for non-profit religious education, devotional habits, and trivia study under fair-use provisions and public domain translations (including the King James Version - KJV, World English Bible - WEB, and authorized modern versions). All copyrights in translated Scripture texts belong to their respective copyright holders.
                </p>
                <p>
                  <strong>License to Host Submissions:</strong> By submitting questions, trivia explanations, or quiz packs through Host Studio, you grant Bible Contest a royalty-free, perpetual, worldwide license to store, format, index, and present those questions within our educational catalog.
                </p>
              </div>
            </section>

            {/* Section 08: Virtual Badges & Contest Prizes */}
            <section id="prizes-badges" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">08</span>
                <h2 className="legal-section-title">Virtual Badges, Streaks & Contest Recognitions</h2>
              </div>
              <div className="legal-prose">
                <p>
                  Bible Contest includes gamification elements such as Devotion Streaks, Trophy Badges, Level Progressions, and Leaderboard Ranks:
                </p>
                <ul>
                  <li><strong>Non-Monetary Nature:</strong> In-game points, virtual coins, streak counts, and digital trophies possess <strong>no real-world monetary or cash value</strong>. They cannot be redeemed, sold, exchanged, or transferred outside the application.</li>
                  <li><strong>No Gambling or Lottery:</strong> Bible Contest is strictly a skill-based, educational trivia platform. Participation does not constitute gambling, betting, or a game of chance.</li>
                  <li><strong>Sponsored Church Honors:</strong> Any physical plaques, bibles, or church recognitions provided by local ministry chapters are managed solely by those third-party organizations. Bible Contest disclaims all warranties regarding third-party physical distributions.</li>
                </ul>
              </div>
            </section>

            {/* Section 09: Suspension & Account Erasure */}
            <section id="termination-erasure" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">09</span>
                <h2 className="legal-section-title">Account Suspension, Termination & Erasure Rights</h2>
              </div>
              <div className="legal-prose">
                <p>
                  We are committed to empowering users with transparent control over their account lifecycle in strict compliance with Google Play Data Safety policies and Apple App Store Guideline 5.1.1(v):
                </p>
                <div className="legal-callout-action">
                  <div className="legal-callout-text">
                    <h4>Self-Service Account Deletion</h4>
                    <p>
                      You may close your account and permanently purge your authentication records, phone numbers, email addresses, and leaderboard statistics at any time without penalty.
                    </p>
                  </div>
                  <Link to={ROUTES.LEGAL.DELETION_REQUEST} className="legal-callout-btn">
                    Account Erasure Portal →
                  </Link>
                </div>
                <h4 style={{ margin: "16px 0 8px 0", color: "var(--clr-text-1)" }}>Involuntary Termination:</h4>
                <p>
                  Bible Contest reserves the right, at its sole discretion, to immediately suspend or permanently ban any account found to have violated these Terms, engaged in cheating, manipulated system timers, or engaged in abusive conduct toward others. Banned accounts forfeit all associated streak counts, rankings, and badges.
                </p>
              </div>
            </section>

            {/* Section 10: Service Availability & Latency */}
            <section id="disclaimers" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">10</span>
                <h2 className="legal-section-title">Service Availability & Real-Time Latency Disclaimers</h2>
              </div>
              <div className="legal-prose">
                <p>
                  The services are provided on an <strong>“AS IS”</strong> and <strong>“AS AVAILABLE”</strong> basis. To the fullest extent permissible by law, Bible Contest disclaims all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.
                </p>
                <div className="legal-summary-box" style={{ background: "#F8FAFC", borderLeftColor: "#64748B" }}>
                  <div className="legal-summary-box-title">
                    <span>📡</span> Network Latency & Disconnections Notice
                  </div>
                  Live competitive trivia requires real-time device-to-cloud synchronization. Bible Contest is not liable for contest failures, missed questions, or speed-bonus losses resulting from local carrier dropouts, weak Wi-Fi, device clock skew, or hardware interruptions.
                </div>
                <p>
                  We do not warrant that the application will operate uninterrupted or error-free at all times. Scheduled maintenance, infrastructure updates, and unforeseen outages may occur.
                </p>
              </div>
            </section>

            {/* Section 11: Limitation of Liability */}
            <section id="liability" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">11</span>
                <h2 className="legal-section-title">Limitation of Liability & Indemnification</h2>
              </div>
              <div className="legal-prose">
                <p>
                  To the maximum extent permitted by applicable law, in no event shall Bible Contest, its trustees, developers, volunteers, or ministry affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data, loss of goodwill, or service interruption, arising out of or in connection with your access to or inability to access the service.
                </p>
                <p>
                  <strong>Indemnification:</strong> You agree to defend, indemnify, and hold harmless Bible Contest, its officers, directors, volunteers, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of your breach of these Terms, your user content, or your violation of any third-party rights.
                </p>
              </div>
            </section>

            {/* Section 12: Governing Law & Contact */}
            <section id="governing-law" className="legal-section">
              <div className="legal-section-header">
                <span className="legal-section-badge">12</span>
                <h2 className="legal-section-title">Governing Law, Jurisdiction & Contact Information</h2>
              </div>
              <div className="legal-prose">
                <p>
                  These Terms and Conditions shall be governed by and construed in accordance with the laws of <strong>Andhra Pradesh, India</strong>, without regard to its conflict of law principles.
                </p>
                <p>
                  Any dispute, controversy, or claim arising out of or relating to these Terms or your use of Bible Contest shall first be submitted to good-faith Christian conciliation and mediation. Should formal proceedings be necessary, the competent courts located in Andhra Pradesh, India shall have exclusive jurisdiction.
                </p>

                <div className="legal-summary-box" style={{ background: "#F8FAFC", borderLeftColor: "#64748B" }}>
                  <p style={{ margin: "0 0 6px 0" }}>
                    <strong>Bible Contest Ministry — Legal & Compliance Desk</strong>
                  </p>
                  <p style={{ margin: "0 0 4px 0" }}>
                    Inquiries & Tournament Oversight: <a href="mailto:support@biblecontest.app" style={{ color: "var(--clr-primary)", fontWeight: 700 }}>support@biblecontest.app</a>
                  </p>
                  
                  <p style={{ margin: 0 }}>
                    Official Web Portal: <a href="https://biblecontest.app" target="_blank" rel="noopener noreferrer">https://biblecontest.app</a>
                  </p>
                </div>

                <p style={{ marginTop: "16px" }}>
                  We reserve the right to amend these Terms and Conditions at any time. Material revisions will be highlighted on our platform or communicated via in-app banner prior to taking effect. Your continued use of Bible Contest following the posting of revised Terms signifies your full acceptance of the updates.
                </p>
              </div>
            </section>
          </article>
        </main>
      </div>

      {/* ─── Portal Footer ─── */}
      <footer className="privacy-portal-footer">
        <div className="privacy-footer-inner">
          <div>
            © {new Date().getFullYear()} Bible Contest Ministry. Dedicated to the glory of God and the study of His Word.
          </div>
          <div className="privacy-footer-links">
            <Link to={ROUTES.LEGAL.PRIVACY}>Privacy Policy</Link>
            <Link to={ROUTES.LEGAL.TERMS}>Terms of Service</Link>
            <Link to={ROUTES.LEGAL.DELETION_REQUEST}>Account Erasure Portal</Link>
            <Link to={ROUTES.PUBLIC.SUPPORT}>Support Center</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditionsPage;
